/**
 * Hook for managing table pagination state and logic
 */

import { useCallback, useMemo, useState } from 'react';
import type { PaginationConfig, RateShopDataRow } from '../types/rate-shop-types';

interface UseTablePaginationProps {
  data: RateShopDataRow[];
  pageSize?: number;
}

interface UseTablePaginationReturn {
  paginatedData: RateShopDataRow[];
  paginationConfig: PaginationConfig;
  goToPage: (page: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  resetToFirstPage: () => void;
}

export function useTablePagination({
  data,
  pageSize = 31,
}: UseTablePaginationProps): UseTablePaginationReturn {
  const [currentPage, setCurrentPage] = useState(1);

  const paginationConfig = useMemo((): PaginationConfig => {
    const totalPages = Math.ceil(data.length / pageSize);
    return {
      currentPage,
      pageSize,
      totalPages,
    };
  }, [data.length, pageSize, currentPage]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= paginationConfig.totalPages) {
        setCurrentPage(page);
      }
    },
    [paginationConfig.totalPages],
  );

  const goToPrevious = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const goToNext = useCallback(() => {
    if (currentPage < paginationConfig.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, paginationConfig.totalPages]);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    paginatedData,
    paginationConfig,
    goToPage,
    goToPrevious,
    goToNext,
    resetToFirstPage,
  };
}
